import Vue from "vue";
import BroadcastBox from "@/components-model/BroadcastBox/BroadcastBox.vue";
import SupabaseApi from "@/api/SupabaseApi";
import APP_CAMERA_POSITIONS from "@/constants/APP_CAMERA_POSITIONS";

// 상주 3D 모델 호스트 — BroadcastBox(sources=[], without-outer)를 앱 생애 1회만 생성.
//  페이지는 mountInto(slot)로 노드(캔버스)를 빌려오고 park()로 회수(리로드 없이 DOM 리페어런팅).
//  init은 ensureInit로 1회만(GLB/WebGL 재로드 방지). 페이지 이동 = appendChild + resize + 카메라만.
//  로그인 '내 펫'을 보여주는 페이지들끼리 공유(같은 펫). /on-air(토큰 다른 펫)는 별개 인스턴스.

let _root = null; // new Vue 루트(마운트 컨테이너)
let _box = null; // BroadcastBox 인스턴스(api)
let _parking = null; // 화면 밖 대기 홀더(미사용 시 여기 보관)
let _inited = false;
let _initing = null;

function ensureInstance() {
  if (_box) return;
  _parking = document.createElement("div");
  _parking.style.cssText = "position:fixed;left:-99999px;top:0;width:0;height:0;overflow:hidden;";
  document.body.appendChild(_parking);
  _root = new Vue({
    // cameraControl:false → 마우스 이벤트 통과(OrbitControls 미동작), 카메라 고정. 미리보기 전용이라 드래그 불필요.
    render: (h) => h(BroadcastBox, { props: { sources: [], withOuter: false, size: 500, cameraControl: false } }),
  }).$mount();
  _parking.appendChild(_root.$el);
  _box = _root.$children[0];
}

// BroadcastBox 인스턴스(init/resize/startIdle/executeInstruction/restoreCamera/... 호출용)
export function api() {
  ensureInstance();
  return _box;
}

// 모델 노드를 페이지 슬롯(빈 div)으로 이동. Vue가 관리하지 않는 컨테이너여야 안전.
export function mountInto(el) {
  ensureInstance();
  if (el) el.appendChild(_box.$el);
}

// 대기 홀더로 회수(페이지 떠날 때).
export function park() {
  ensureInstance();
  if (_parking) _parking.appendChild(_box.$el);
}

// 모델 init 1회만. modelIndex 없으면 활성 펫에서 조회. 펫 없으면 no-op(false).
export async function ensureInit(modelIndex, cameraPosition) {
  ensureInstance();
  if (_inited) return true;
  if (_initing) return _initing;
  _initing = (async () => {
    const idx = modelIndex || (await SupabaseApi.PetProfile.fetchModelIndex());
    if (!idx) return false;
    await _box.init(idx, cameraPosition || APP_CAMERA_POSITIONS.HOME_ZOOM);
    // 리액션 해석 맵(system + 내 것)도 호스트가 자족 로드 — 어느 페이지에서 먼저 attach하든
    // executeInstruction이 동작하도록 (방문 순서 의존 E_INSTRUCTION_NOT_FOUND 방지).
    // 리액션 페이지(MotionPage)의 setReactions 주입은 생성/수정 후 "갱신"용으로 유지.
    try {
      const reactions = await SupabaseApi.Reaction.fetchAll();
      _box.setReactions(SupabaseApi.Reaction.toMaps(reactions));
    } catch (e) {
      console.warn("modelHost: 리액션 맵 로드 실패 (재생 시 미해석 가능)", e);
    }
    _inited = true;
    return true;
  })();
  return _initing;
}

export function isInited() {
  return _inited;
}

// idle은 앱 생애 1회만 시작(무한 루프라 재시작 불필요). 페이지가 순서/타이밍 신경 안 쓰게.
let _idle = false;
function ensureIdle() {
  if (_idle) return;
  api().startIdle("action-only--{{loop}}idle-40");
  _idle = true;
}

// 페이지-부착 관심사 통합: 슬롯으로 이동 → init 1회 → 크기/카메라 → idle 보장.
//  페이지는 attach/detach만 부르면 되고, 순서·init-once·idle 타이밍은 여기서 책임.
export async function attach(slot, { size = 500, camera, modelIndex } = {}) {
  ensureInstance();
  mountInto(slot); // 먼저 화면 안 슬롯으로 붙여 init/렌더가 보이는 곳에서 일어나게
  const ok = await ensureInit(modelIndex, camera);
  if (!ok) return false; // 펫/모델 없음
  api().resize(size);
  api().restoreCamera();
  ensureIdle();
  return true;
}

// 페이지 떠날 때: 대기 홀더로 회수.
export function detach() {
  park();
}
